import {ReactNode, useCallback, useMemo, useState} from "react";
import {
    coverViewPort,
} from "@/encounter/model/ViewPort.ts";
import {
    EncounterViewPortContext,
    EncounterViewPortContextValue,
} from "@/encounter/viewport/EncounterViewPortHooks.ts";
import {defaultStageSize, useGameMasterViewPort} from "@/encounter/viewport/useGameMasterViewPort.ts";
import {usePlayerViewPortSyncState} from "./PlayerViewPortHooks.ts";
import type Position from "@/util/Position.ts";

export function EncounterViewPortProvider({children}: { children: ReactNode }) {
    const [playerViewPortLocked, setPlayerViewPortLocked] = useState(false);
    const {
        playerViewPort,
        getPlayerViewPort,
        scheduleViewPortUpdate,
        persistViewPort,
        cancelScheduledUpdates,
    } = usePlayerViewPortSyncState();
    const initialMapTransform = coverViewPort(playerViewPort, defaultStageSize);
    const {
        mapTransform,
        getCurrentViewPort,
        updateMapTransform,
        resizeStage,
        focusPlayerViewPort,
        focusWorldPosition: focusGameMasterWorldPosition,
    } = useGameMasterViewPort({
        initialMapTransform,
        playerViewPortLocked,
        getPlayerViewPort,
        scheduleViewPortUpdate,
    });

    const persistCurrentViewPort = useCallback(() => {
        cancelScheduledUpdates();
        if (!playerViewPortLocked) persistViewPort(getCurrentViewPort());
    }, [cancelScheduledUpdates, getCurrentViewPort, persistViewPort, playerViewPortLocked]);

    const togglePlayerViewPortLock = useCallback(() => {
        cancelScheduledUpdates();
        persistViewPort(getCurrentViewPort());
        setPlayerViewPortLocked(locked => !locked);
    }, [cancelScheduledUpdates, getCurrentViewPort, persistViewPort]);

    const focusWorldPosition = useCallback((position: Position) => {
        cancelScheduledUpdates();
        focusGameMasterWorldPosition(position);
        persistViewPort({...getPlayerViewPort(), centerX: position.x, centerY: position.y});
    }, [cancelScheduledUpdates, focusGameMasterWorldPosition, getPlayerViewPort, persistViewPort]);

    const value = useMemo<EncounterViewPortContextValue>(() => ({
        mapTransform,
        updateMapTransform,
        resizeStage,
        persistCurrentViewPort,
        playerViewPort,
        playerViewPortLocked,
        togglePlayerViewPortLock,
        focusPlayerViewPort,
        focusWorldPosition,
    }), [
        focusPlayerViewPort,
        focusWorldPosition,
        mapTransform,
        persistCurrentViewPort,
        playerViewPort,
        playerViewPortLocked,
        resizeStage,
        togglePlayerViewPortLock,
        updateMapTransform,
    ]);

    return <EncounterViewPortContext.Provider value={value}>
        {children}
    </EncounterViewPortContext.Provider>;
}
